import { ReportActionStatus, ReportInternalType } from '@/__generated__/graphql';
import { DatePickerIcon } from '@/assets/icons/DatePickerIcon';
import { useReportFormLogic } from '@/components/Analysis/ReportsRevamped/hooks/useReportFormLogic';
import { ReportCreationMode, ReportGroupDestination } from '@/constants';
import { abyssColors } from '@/theme/colors';
import { formatEnumForDisplay } from '@/utils';
import { ArrowDropDown, InfoOutlined } from '@mui/icons-material';
import { Autocomplete, Tooltip, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { GenericDialog } from '../../GenericDialog';
import { ReportFormRadioGroup, datePickerSlotProps } from '../styles';
import {
  ReportDatePicker,
  ReportFormTextField,
  actionsStyles,
  autocompleteTextFieldStyles,
  contentStyles,
  paperStyles,
  radioButtonStyles,
  titleStyles,
} from './styles';
import { useFeatureFlag } from '@/hooks';
import { InvalidScopeDialog } from './InvalidScopeDialog';

type Props = {
  open: boolean;
  title: string;
  handleClose: () => void;
  reportCreated?: () => void;
  internalType: ReportInternalType;
  componentIds?: string[];
  openComponentReportDialog?: () => void;
  id?: string;
};

const actionStatuses = [
  ReportActionStatus.Backlog,
  ReportActionStatus.InProgress,
  ReportActionStatus.Complete,
];

export const AddToComponentReport = ({
  open,
  title,
  handleClose,
  reportCreated = () => {},
  internalType,
  componentIds,
  openComponentReportDialog,
  id,
}: Props) => {
  const isWorkInProgress = useFeatureFlag('work-in-progress');

  const {
    allWorkpackNames,
    reportName,
    actionStatus,
    dueDate,
    isFocused,
    workpackNumber,
    destination,
    isInValidReportEntry,
    submittingReportState,
    numberOfReports,
    totalComponentsCount,
    filteringNotApplied,
    creationMode,
    groupReportName,
    openInvalidScopeDialog,
    setOpenInvalidScopeDialog,
    filterOptions,
    getReportNameError,
    handleReportNameChange,
    handleGroupReportNameChange,
    handleReportDueDateChange,
    handleReportActionStatusChange,
    setIsFocused,
    handleAddOrCreateReport,
    handleWorkpackSelection,
    handleDestinationChange,
    handleGroupNameChange,
    handleCreationModeChanged,
    handleByPassInvalidScopeDialog,
    handleCancelInvalidScopeDialog,
    handleCloseAndResetStates,
  } = useReportFormLogic({
    handleClose,
    reportCreated,
    internalType,
    componentIds,
    openComponentReportDialog,
  });

  return (
    <>
      <GenericDialog
        id={id}
        open={open}
        onClose={handleCloseAndResetStates}
        showCloseIcon
        title={title}
        paperStyles={paperStyles}
        contentStyles={contentStyles}
        titleStyles={titleStyles}
        actionsStyles={actionsStyles}
        content={
          <>
            <Stack gap="2.4rem">
              <Stack gap="1.2rem">
                {isWorkInProgress && (
                  <FormControl fullWidth sx={radioButtonStyles}>
                    <ReportFormRadioGroup
                      defaultValue={ReportCreationMode.Single}
                      onChange={handleCreationModeChanged}
                      row
                    >
                      <Stack direction="row" gap="2.4rem" alignItems="center">
                        <FormControlLabel
                          value={ReportCreationMode.Single}
                          control={<Radio disableRipple />}
                          label="One report"
                        />
                        <FormControlLabel
                          value={ReportCreationMode.Seperate}
                          control={<Radio disableRipple />}
                          label="Seperate reports"
                          disabled={totalComponentsCount === 1}
                        />
                      </Stack>
                    </ReportFormRadioGroup>
                  </FormControl>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    '& span': {
                      fontWeight: 500,
                    },
                  }}
                >
                  {filteringNotApplied ? (
                    <span>
                      Note: Cannot create report for all components. Please apply filters or select
                      some components to create report.
                    </span>
                  ) : (
                    <span>
                      Note: You will be creating <span>{numberOfReports}</span> report
                      {numberOfReports === 1 ? '' : 's'} for <span>{totalComponentsCount}</span>{' '}
                      component
                      {totalComponentsCount === 1 ? '' : 's'}.
                    </span>
                  )}
                </Typography>
              </Stack>

              <Stack gap={5}>
                <Grid container columnSpacing="4rem" rowSpacing="2.4rem">
                  {isWorkInProgress && (
                    <Grid size={{ xs: 6 }}>
                      <Autocomplete
                        defaultValue={ReportGroupDestination.GeneralReports}
                        value={destination}
                        disabled
                        options={Object.values(ReportGroupDestination)}
                        disableClearable
                        onChange={(_, value) => {
                          handleDestinationChange(value ?? '');
                        }}
                        renderInput={(parameters) => (
                          <TextField
                            {...parameters}
                            label="Group destination"
                            variant="standard"
                            sx={autocompleteTextFieldStyles}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  <Grid size={{ xs: 6 }}>
                    {creationMode === ReportCreationMode.Single && (
                      <ReportFormTextField
                        label="Report Name"
                        variant="standard"
                        fullWidth
                        error={isFocused && !!getReportNameError(reportName)}
                        helperText={isFocused ? getReportNameError(reportName) : ''}
                        onChange={handleReportNameChange}
                        onFocus={() => setIsFocused(true)}
                        value={reportName}
                        slotProps={{
                          input: {
                            inputProps: {
                              maxLength: 200,
                            },
                          },
                        }}
                      />
                    )}
                    {creationMode === ReportCreationMode.Seperate && (
                      <ReportFormTextField
                        label="Group Name"
                        variant="standard"
                        fullWidth
                        onChange={handleGroupNameChange}
                        onFocus={() => setIsFocused(true)}
                        slotProps={{
                          input: {
                            inputProps: {
                              maxLength: 200,
                            },
                          },
                        }}
                      />
                    )}
                  </Grid>
                  {creationMode === ReportCreationMode.Seperate && (
                    <Grid size={{ xs: 6 }}>
                      <ReportFormTextField
                        label={
                          <Stack direction="row" alignItems="center" gap="0.6rem">
                            <span>Report name</span>
                            <Tooltip
                              title="This will be standard report name"
                              placement="top"
                              arrow
                            >
                              <span>
                                <InfoOutlined />
                              </span>
                            </Tooltip>
                          </Stack>
                        }
                        variant="standard"
                        fullWidth
                        error={isFocused && !!getReportNameError(groupReportName)}
                        helperText={isFocused ? getReportNameError(groupReportName) : ''}
                        onChange={handleGroupReportNameChange}
                        onFocus={() => setIsFocused(true)}
                        slotProps={{
                          input: {
                            inputProps: {
                              maxLength: 200,
                            },
                          },
                        }}
                      />
                    </Grid>
                  )}
                  <Grid size={{ xs: 6 }}>
                    <Autocomplete
                      freeSolo
                      forcePopupIcon
                      disableClearable
                      defaultValue=""
                      autoHighlight
                      filterOptions={filterOptions}
                      value={workpackNumber}
                      options={allWorkpackNames}
                      onChange={(_, value: unknown) => {
                        return handleWorkpackSelection(value as string);
                      }}
                      popupIcon={<ArrowDropDown sx={{ color: abyssColors.primary[400] }} />}
                      renderInput={(parameters) => (
                        <TextField
                          {...parameters}
                          label="Work pack number"
                          variant="standard"
                          sx={autocompleteTextFieldStyles}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Autocomplete
                      value={formatEnumForDisplay(actionStatus)}
                      options={actionStatuses.map((status) => formatEnumForDisplay(status))}
                      disableClearable
                      onChange={(_, value) => {
                        handleReportActionStatusChange(value ?? '');
                      }}
                      renderInput={(parameters) => (
                        <TextField
                          {...parameters}
                          label="Status"
                          variant="standard"
                          sx={autocompleteTextFieldStyles}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Stack alignItems="end" direction="row" height="100%">
                      <ReportDatePicker
                        format="dd MMM yyyy"
                        disablePast
                        label="Due date"
                        slotProps={datePickerSlotProps}
                        onChange={handleReportDueDateChange}
                        value={dueDate ? new Date(dueDate) : undefined}
                        slots={{
                          openPickerIcon: DatePickerIcon,
                        }}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
          </>
        }
        actions={
          <>
            <Button size="medium" onClick={handleCloseAndResetStates} variant="text">
              Cancel
            </Button>
            <Button
              size="medium"
              onClick={handleAddOrCreateReport}
              disabled={
                isInValidReportEntry || submittingReportState === 'Loading' || filteringNotApplied
              }
              variant="contained"
            >
              Confirm
            </Button>
          </>
        }
      />
      <InvalidScopeDialog
        open={openInvalidScopeDialog}
        setOpen={setOpenInvalidScopeDialog}
        handleCancel={handleCancelInvalidScopeDialog}
        handleContinue={handleByPassInvalidScopeDialog}
        loading={submittingReportState === 'Loading'}
      />
    </>
  );
};
